import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorState } from '@/components/ui';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Replaces the default treatment; receives the thrown value and a reset callback. */
  fallback?: ((error: unknown, reset: () => void) => ReactNode) | undefined;
  onError?: ((error: unknown, info: ErrorInfo) => void) | undefined;
}

interface ErrorBoundaryState {
  error: unknown;
}

/**
 * Top-level render guard. React has no hook equivalent, so this stays a class component.
 * Route-level failures are handled by the router's own `errorElement`; this one exists to keep a
 * crash anywhere else from leaving the user on a blank page.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    // Kept until an observability sink exists (Sprint 3): a swallowed crash is undebuggable.
    console.error('Erro não tratado na interface', error, info.componentStack);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;

    if (error === null) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <ErrorState
        error={error}
        title="Algo deu errado"
        description="A tela não pôde ser exibida. Tente novamente ou recarregue a página."
        onRetry={this.reset}
      />
    );
  }
}
